import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import axios from 'axios';
import * as xlsx from 'xlsx';
import Papa from 'papaparse';

const REQUIRED_COLUMNS = [
  { key: 'contact_number', label: 'Contact Number' },
  { key: 'person_name', label: 'Person Name' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'person_role', label: 'Person Role' }
];

function CustomListModal({ show, onHide, userid, onSuccess }) {
  const [listName, setListName] = useState('');
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [mapping, setMapping] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setListName('');
    setFile(null);
    setHeaders([]);
    setParsedData([]);
    setMapping({});
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);

    const fileReader = new FileReader();

    const isExcel = selectedFile.name.match(/\.(xls|xlsx)$/i);
    const isCsv = selectedFile.name.match(/\.(csv)$/i);

    if (isExcel) {
      fileReader.onload = (evt) => {
        const bstr = evt.target.result;
        const wb = xlsx.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = xlsx.utils.sheet_to_json(ws, { header: 1 });
        if (data.length > 0) {
          const fileHeaders = data[0].map(String);
          setHeaders(fileHeaders);
          
          const records = xlsx.utils.sheet_to_json(ws);
          const filteredData = records.filter(row => Object.values(row).some(val => val !== null && val !== undefined && String(val).trim() !== ''));
          setParsedData(filteredData);
          autoMapHeaders(fileHeaders);
        }
      };
      fileReader.readAsBinaryString(selectedFile);
    } else if (isCsv) {
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (results) => {
          if (results.meta.fields && results.data) {
            setHeaders(results.meta.fields);
            const filteredData = results.data.filter(row => Object.values(row).some(val => val !== null && val !== undefined && String(val).trim() !== ''));
            setParsedData(filteredData);
            autoMapHeaders(results.meta.fields);
          }
        }
      });
    } else {
      alert("Unsupported file format. Please upload a CSV or Excel file.");
    }
  };

  const autoMapHeaders = (fileHeaders) => {
    let initialMapping = {};
    REQUIRED_COLUMNS.forEach(reqCol => {
      const match = fileHeaders.find(h => 
        h.toLowerCase().replace(/[^a-z0-9]/g, '') === reqCol.label.toLowerCase().replace(/[^a-z0-9]/g, '')
      );
      if (match) {
        initialMapping[reqCol.key] = match;
      } else {
        initialMapping[reqCol.key] = '';
      }
    });
    setMapping(initialMapping);
  };

  const handleMappingChange = (reqKey, fileHeader) => {
    setMapping(prev => ({
      ...prev,
      [reqKey]: fileHeader
    }));
  };

  const handleSave = async () => {
    if (!listName.trim()) {
      alert("Please enter a list name.");
      return;
    }
    if (parsedData.length === 0) {
      alert("Please upload a file with data.");
      return;
    }

    const unmappedRequired = REQUIRED_COLUMNS.filter(col => !mapping[col.key]);
    if (unmappedRequired.length > 0) {
      alert(`Please map all required columns. Missing: ${unmappedRequired.map(c => c.label).join(', ')}`);
      return;
    }

    setIsUploading(true);

    const records = parsedData.map(row => {
      let record = {};
      REQUIRED_COLUMNS.forEach(reqCol => {
        const fileColumnName = mapping[reqCol.key];
        record[reqCol.key] = row[fileColumnName] ? String(row[fileColumnName]) : '';
      });
      return record;
    });

    try {
      const response = await axios.post('/customlist/save', {
        name: listName,
        userid: userid,
        records: records
      });

      if (response.data && response.data.success) {
        if (onSuccess) onSuccess(response.data.listId);
        handleClose();
      } else {
        alert("Failed to save the list.");
        setIsUploading(false);
      }
    } catch (error) {
      console.error("Save list error:", error);
      alert("An error occurred while saving the list.");
      setIsUploading(false);
    } 
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">Create Custom List</Modal.Title>
      </Modal.Header>
      <Modal.Body className='p-3'>
        <Form.Group className="mb-3">
          <Form.Label className='fw-bold'>List Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter list name"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className='fw-bold'>Upload CSV or Excel File <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="file"
            accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
            ref={fileInputRef}
          />
          <Form.Text className="text-muted">
            File must contain headers. Supported formats: .csv, .xls, .xlsx
          </Form.Text>
        </Form.Group>

        {headers.length > 0 && (
          <div className="mt-4">
            <h6 className="fw-bold mb-3">Map Columns</h6>
            <Table bordered size="sm">
              <thead className="table-light">
                <tr>
                  <th>Required Fields</th>
                  <th>Your File Columns</th>
                </tr>
              </thead>
              <tbody>
                {REQUIRED_COLUMNS.map((reqCol) => (
                   <tr key={reqCol.key}>
                    <td className="align-middle fw-semibold">
                      {reqCol.label} <span className="text-danger">*</span>
                    </td>
                    <td>
                      <Form.Select 
                        value={mapping[reqCol.key] || ''} 
                        onChange={(e) => handleMappingChange(reqCol.key, e.target.value)}
                      >
                        <option value="">-- Select Column --</option>
                        {headers.map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </Form.Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="text-end text-muted small mt-2">
              Found {parsedData.length} records.
            </div>
          </div>
        )}

      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isUploading || parsedData.length === 0}>
          {isUploading ? <><i className="fa fa-spinner fa-pulse me-2"></i>Saving...</> : 'Save List'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CustomListModal;
